import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { BUSINESS_INFO } from "../data";

export default function Navbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cleaningDropdownOpen, setCleaningDropdownOpen] = useState(false);
  const [mobileCleaningOpen, setMobileCleaningOpen] = useState(true);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setCleaningDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isCleaningActive = 
    location.pathname === "/sommerhusrengoering" || 
    location.pathname === "/airbnb-rengoering" ||
    location.pathname === "/toemning-af-doedsbo" ||
    location.pathname === "/rengoering-af-hus-eller-lejlighed";

  return (
    <header className="sticky top-0 z-50 bg-[#FAF7F2]/95 backdrop-blur-md border-b border-[#EADFCF] shadow-xs">
      {/* Top info bar */}
      <div className="bg-[#2C2523] text-[#EADFCF] text-xs py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center sm:justify-between text-center sm:text-left gap-2">
          <div className="flex items-center justify-center sm:justify-start gap-4 flex-wrap">
            <span className="hidden sm:flex items-center gap-1.5 text-xs">
              <i className="fa-solid fa-location-dot text-[#C2AB8E]" aria-hidden="true"></i>
              <span>{BUSINESS_INFO.area} • {BUSINESS_INFO.postalAndCity}</span>
            </span>
            <span className="hidden sm:inline text-[#5C524C]">|</span>
            <a 
              href={`mailto:${BUSINESS_INFO.email}`} 
              className="hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[#C2AB8E]" aria-hidden="true"></i>
              <span className="whitespace-nowrap">{BUSINESS_INFO.email}</span>
            </a>
          </div>

          <div className="flex items-center justify-center sm:justify-start gap-4">
            <a 
              href={`tel:${BUSINESS_INFO.phone}`} 
              className="font-semibold text-[#FFFDF9] hover:text-[#C2AB8E] transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#C2AB8E]" aria-hidden="true"></i>
              <span className="whitespace-nowrap">Tlf. {BUSINESS_INFO.phoneFormatted}</span>
            </a>
            <div className="flex items-center gap-2.5 pl-2 border-l border-[#4B3C2D]">
              <a 
                href={BUSINESS_INFO.facebookUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-white transition-colors text-sm"
                aria-label="Ses Multi Service på Facebook"
              >
                <i className="fa-brands fa-facebook" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-28">
          {/* Logo */}
          <Logo variant="horizontal" />

          {/* Desktop Nav - Only visible >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center gap-8" aria-label="Hovednavigation">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors py-2 relative ${
                isActive("/") && !isCleaningActive
                  ? "text-[#2C2523] font-semibold"
                  : "text-[#5C524C] hover:text-[#2C2523]"
              }`}
            >
              Forside
              {isActive("/") && !isCleaningActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#887055] rounded-full" />
              )}
            </Link>

            {/* Dropdown for Rengøring */}
            <div 
              className="relative"
              onMouseEnter={() => setCleaningDropdownOpen(true)}
              onMouseLeave={() => setCleaningDropdownOpen(false)}
            >
              <button
                type="button"
                className={`text-sm font-medium transition-colors py-2 flex items-center gap-1.5 cursor-pointer ${
                  isCleaningActive ? "text-[#2C2523] font-semibold" : "text-[#5C524C] hover:text-[#2C2523]"
                }`}
                onClick={() => setCleaningDropdownOpen(!cleaningDropdownOpen)}
                aria-expanded={cleaningDropdownOpen}
              >
                <span>Rengøring</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${cleaningDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
                {isCleaningActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#887055] rounded-full" />
                )}
              </button>

              {/* Dropdown Menu */}
              {cleaningDropdownOpen && (
                <div className="absolute left-0 mt-1 w-60 bg-[#FAF7F2] border border-[#EADFCF] rounded-xl shadow-lg py-2 z-50 animate-in fade-in duration-150">
                  <Link
                    to="/sommerhusrengoering"
                    className={`block px-4 py-2.5 text-sm transition-colors ${
                      location.pathname === "/sommerhusrengoering"
                        ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                        : "text-[#2C2523] hover:bg-[#F4EFE6]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="fa-solid fa-house-chimney text-[#887055] w-4 text-center" aria-hidden="true"></i>
                      <span>Sommerhusrengøring</span>
                    </div>
                  </Link>
                  <Link
                    to="/airbnb-rengoering"
                    className={`block px-4 py-2.5 text-sm transition-colors ${
                      location.pathname === "/airbnb-rengoering"
                        ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                        : "text-[#2C2523] hover:bg-[#F4EFE6]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="fa-solid fa-key w-4 text-center text-[#887055]" aria-hidden="true"></i>
                      <span>Airbnb-rengøring</span>
                    </div>
                  </Link>
                  <Link
                    to="/rengoering-af-hus-eller-lejlighed"
                    className={`block px-4 py-2.5 text-sm transition-colors ${
                      location.pathname === "/rengoering-af-hus-eller-lejlighed"
                        ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                        : "text-[#2C2523] hover:bg-[#F4EFE6]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="fa-solid fa-house-user w-4 text-center text-[#887055]" aria-hidden="true"></i>
                      <span>Rengøring af hus eller lejlighed</span>
                    </div>
                  </Link>
                  <Link
                    to="/toemning-af-doedsbo"
                    className={`block px-4 py-2.5 text-sm transition-colors ${
                      location.pathname === "/toemning-af-doedsbo"
                        ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                        : "text-[#2C2523] hover:bg-[#F4EFE6]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="fa-solid fa-boxes-packing w-4 text-center text-[#887055]" aria-hidden="true"></i>
                      <span>Tømning af dødsbo & rengøring</span>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/hundeklip-og-vask"
              className={`text-sm font-medium transition-colors py-2 relative ${
                isActive("/hundeklip-og-vask")
                  ? "text-[#2C2523] font-semibold"
                  : "text-[#5C524C] hover:text-[#2C2523]"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <i className="fa-solid fa-dog text-[#887055]" aria-hidden="true"></i>
                <span>Hundeklip & vask</span>
              </span>
              {isActive("/hundeklip-og-vask") && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#887055] rounded-full" />
              )}
            </Link>

            <Link
              to="/om-os"
              className={`text-sm font-medium transition-colors py-2 relative ${
                isActive("/om-os")
                  ? "text-[#2C2523] font-semibold"
                  : "text-[#5C524C] hover:text-[#2C2523]"
              }`}
            >
              Om os
              {isActive("/om-os") && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#887055] rounded-full" />
              )}
            </Link>

            <Link
              to="/kontakt"
              className={`text-sm font-medium transition-colors py-2 relative ${
                isActive("/kontakt")
                  ? "text-[#2C2523] font-semibold"
                  : "text-[#5C524C] hover:text-[#2C2523]"
              }`}
            >
              Kontakt
              {isActive("/kontakt") && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#887055] rounded-full" />
              )}
            </Link>
          </nav>

          {/* Header Action Button Desktop */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href={`tel:${BUSINESS_INFO.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-[#3A302B] hover:bg-[#231C19] text-[#FFFDF9] px-5 py-2.5 rounded-xl text-sm font-medium transition-colors shadow-xs active:scale-[0.98] whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
              <span className="whitespace-nowrap">Ring 40 94 75 40</span>
            </a>
          </div>

          {/* Mobile Burger Toggle - Visible < 1200px */}
          <div className="flex items-center min-[1200px]:hidden gap-2">
            <a
              href={`tel:${BUSINESS_INFO.phone}`}
              className="inline-flex items-center gap-1.5 bg-[#3A302B] text-white px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap"
              aria-label="Ring op"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i>
              <span className="whitespace-nowrap">40 94 75 40</span>
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-xl text-[#2C2523] hover:bg-[#EADFCF] transition-colors cursor-pointer"
              aria-label="Åbn menu"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Navigation (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-[#EADFCF] bg-[#FAF7F2] px-4 pt-4 pb-6 shadow-xl animate-in slide-in-from-top-2 duration-200">
          <div className="flex flex-col gap-2">
            <Link
              to="/"
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                isActive("/") && !isCleaningActive
                  ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                  : "text-[#2C2523] hover:bg-[#F4EFE6]"
              }`}
            >
              Forside
            </Link>

            {/* Mobile Cleaning Accordion */}
            <div className="rounded-xl overflow-hidden border border-[#EADFCF] bg-[#FFFDF9]">
              <button
                type="button"
                onClick={() => setMobileCleaningOpen(!mobileCleaningOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-base font-medium text-[#2C2523] cursor-pointer"
              >
                <span>Rengøring</span>
                <i className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${mobileCleaningOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
              </button>

              {mobileCleaningOpen && (
                <div className="bg-[#FAF7F2] px-2 py-1 border-t border-[#EADFCF] space-y-1">
                  <Link
                    to="/sommerhusrengoering"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      location.pathname === "/sommerhusrengoering"
                        ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                        : "text-[#5C524C] hover:text-[#2C2523]"
                    }`}
                  >
                    <i className="fa-solid fa-house-chimney text-[#887055] w-4 text-center" aria-hidden="true"></i>
                    <span>Sommerhusrengøring</span>
                  </Link>
                  <Link
                    to="/airbnb-rengoering"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      location.pathname === "/airbnb-rengoering"
                        ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                        : "text-[#5C524C] hover:text-[#2C2523]"
                    }`}
                  >
                    <i className="fa-solid fa-key text-[#887055] w-4 text-center" aria-hidden="true"></i>
                    <span>Airbnb-rengøring</span>
                  </Link>
                  <Link
                    to="/rengoering-af-hus-eller-lejlighed"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      location.pathname === "/rengoering-af-hus-eller-lejlighed"
                        ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                        : "text-[#5C524C] hover:text-[#2C2523]"
                    }`}
                  >
                    <i className="fa-solid fa-house-user text-[#887055] w-4 text-center" aria-hidden="true"></i>
                    <span>Rengøring af hus eller lejlighed</span>
                  </Link>
                  <Link
                    to="/toemning-af-doedsbo"
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      location.pathname === "/toemning-af-doedsbo"
                        ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                        : "text-[#5C524C] hover:text-[#2C2523]"
                    }`}
                  >
                    <i className="fa-solid fa-boxes-packing text-[#887055] w-4 text-center" aria-hidden="true"></i>
                    <span>Tømning af dødsbo & rengøring</span>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/hundeklip-og-vask"
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors flex items-center gap-2 ${
                isActive("/hundeklip-og-vask")
                  ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                  : "text-[#2C2523] hover:bg-[#F4EFE6]"
              }`}
            >
              <i className="fa-solid fa-dog text-[#887055]" aria-hidden="true"></i>
              <span>Hundeklip & vask</span>
            </Link>

            <Link
              to="/om-os"
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                isActive("/om-os")
                  ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                  : "text-[#2C2523] hover:bg-[#F4EFE6]"
              }`}
            >
              Om os
            </Link>

            <Link
              to="/kontakt"
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                isActive("/kontakt")
                  ? "bg-[#EADFCF] text-[#2C2523] font-semibold"
                  : "text-[#2C2523] hover:bg-[#F4EFE6]"
              }`}
            >
              Kontakt
            </Link>

            <div className="pt-4 mt-2 border-t border-[#EADFCF] space-y-3">
              <a
                href={`tel:${BUSINESS_INFO.phone}`}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#3A302B] text-[#FFFDF9] py-3 rounded-xl text-base font-medium shadow-xs whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                <span className="whitespace-nowrap">Ring på 40 94 75 40</span>
              </a>
              <Link
                to="/kontakt"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#EADFCF] text-[#2C2523] py-3 rounded-xl text-base font-medium"
              >
                <i className="fa-solid fa-paper-plane text-[#887055]" aria-hidden="true"></i>
                <span>Send en forespørgsel</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
